// Whole words in running text, the browser's copy of
// `resume_doc::words::says` (libs/crates/resume/src/words.rs).
//
// A term is found as a whole word or phrase and never inside a longer
// word: `go` is not in `golang`, and `rn` is not in `intern`. A boundary is
// anything that is not a letter or a digit, read a code point at a time. One
// plural `s` right after the term is allowed. Both strings have each run of
// white space cut to one space first, as the lexicon's `cut` does, so a
// phrase wrapped over two lines is still found.
//
// Both copies are held to libs/crates/resume/testdata/whole_words.json.

// Rust's `char::is_alphanumeric`: Alphabetic, or numeric (Nd, Nl, No).
const WORD_CHARACTER = /^[\p{Alphabetic}\p{N}]$/u;
// Rust's `str::trim` and the lexicon's cut both split on White_Space.
const WHITE_SPACE_RUN = /\p{White_Space}+/gu;

function squash(text: string): string {
    return text.replace(WHITE_SPACE_RUN, " ").trim().toLowerCase();
}

function isWordCharacter(character: string | undefined): boolean {
    return character !== undefined && WORD_CHARACTER.test(character);
}

// The code point that ends just before `index`, or undefined at the start.
function characterBefore(text: string, index: number): string | undefined {
    if (index <= 0) return undefined;
    const low = text.charCodeAt(index - 1);
    const isPairTail = low >= 0xdc00 && low <= 0xdfff;
    const high = text.charCodeAt(index - 2);
    const isPairHead = high >= 0xd800 && high <= 0xdbff;
    return text.slice(isPairTail && isPairHead ? index - 2 : index - 1, index);
}

// The code point that starts at `index`, or undefined at the end.
function characterAt(text: string, index: number): string | undefined {
    const point = text.codePointAt(index);
    return point === undefined ? undefined : String.fromCodePoint(point);
}

// Whether a word ends at `index`: the end of `text`, or a character that is
// not part of a word.
function endsWord(text: string, index: number): boolean {
    return !isWordCharacter(characterAt(text, index));
}

function isWholeAt(text: string, start: number, end: number): boolean {
    const beforeOk = !isWordCharacter(characterBefore(text, start));
    const afterOk = endsWord(text, end) || (text[end] === "s" && endsWord(text, end + 1));
    return beforeOk && afterOk;
}

/** Whether `text` names `term` as a whole word, whatever the case of either. */
export function says(text: string, term: string): boolean {
    const haystack = squash(text);
    const needle = squash(term);
    if (needle === "") return false;

    let from = 0;
    for (;;) {
        const start = haystack.indexOf(needle, from);
        if (start === -1) return false;
        if (isWholeAt(haystack, start, start + needle.length)) return true;
        // One unit on is enough: a term never starts inside a surrogate pair.
        from = start + 1;
    }
}
